/**
 * Striker Run API helpers. Endless run flow: start today's session, then report each finished run. The
 * server owns the economy — coins depend only on server-clamped goals/pickups + the per-day cap, never on
 * a client-supplied amount. distance/cause are advisory (analytics/UI) only.
 */

import type { useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import { STRIKER_RUN_GAME_KEY } from '../constants/strikerRun.constants';

type ApiClient = ReturnType<typeof useApiClient>;

export interface StrikerRunStart {
  coinsPerGoal: number;
  maxPaidGoalsPerRun: number;
  dailyCoinCap: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestGoals: number;
}

export interface StrikerRunEndResult {
  coinsAwarded: number;
  /** New server coin balance; omitted for anonymous users (never banked). */
  coins?: number;
  coinsLeftToday: number;
  capReached: boolean;
  bestGoals: number;
  newBest: boolean;
}

export async function startStrikerRun(apiClient: ApiClient): Promise<StrikerRunStart> {
  const response = await apiClient<{ success: boolean; data: StrikerRunStart; message?: string }>(
    API_ENDPOINTS.games.runStart(STRIKER_RUN_GAME_KEY),
    { method: 'POST', data: {} },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not start the game');
  return payload.data;
}

export async function recordStrikerRunEnd(
  apiClient: ApiClient,
  args: { goals: number; pickups: number },
): Promise<StrikerRunEndResult> {
  const response = await apiClient<{ success: boolean; data: StrikerRunEndResult; message?: string }>(
    API_ENDPOINTS.games.runEnd(STRIKER_RUN_GAME_KEY),
    { method: 'POST', data: args },
  );
  const payload = response.data;
  if (!payload.success) throw new Error(payload.message ?? 'Could not record the run');
  return payload.data;
}
